// Recovery from a stale build (a tab opened before a deploy). Route screens are code-split: an old
// tab lazy-loads a chunk of the build it started with, and once that chunk is gone the dynamic import
// fails. Reloading picks up the current index.html and its chunks. The reload happens at most once
// per window, so a chunk that keeps failing (an outage, a broken build) shows the route error screen
// instead of reloading in a loop.

/** sessionStorage key holding the time (ms since the epoch) of the last recovery reload. */
export const STALE_CHUNK_RELOAD_KEY = "strust:stale-chunk-reload";

/** A second failure within this window after a recovery reload is not reloaded again. */
export const STALE_CHUNK_RELOAD_WINDOW_MS = 60_000;

// Chromium, Firefox and Safari messages for a failed dynamic import, Vite's failed CSS preload, and
// webpack-style chunk errors (`ChunkLoadError`).
const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (?:CSS )?chunk [\w-]+ failed/i;

/** Whether `error` is a failed load of a code-split chunk (JavaScript or CSS). */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === "ChunkLoadError" || CHUNK_ERROR.test(error.message);
}

export interface StaleChunkReloadOptions {
  readonly storage?: Pick<Storage, "getItem" | "setItem"> | undefined;
  readonly reload?: () => void;
  readonly now?: () => number;
  /** Whether the browser is online; an offline blip is not a stale build, so it is not reloaded. */
  readonly online?: () => boolean;
}

/**
 * Reloads the page unless a recovery reload already happened within the window; returns whether it
 * reloaded. Without a usable sessionStorage it never reloads (nothing would stop a loop), nor while
 * the browser is offline (the reload would fail too): the route error screen offers the reload.
 */
export function reloadOnceForStaleChunk({
  storage = sessionStorageOrUndefined(),
  reload = () => {
    window.location.reload();
  },
  now = Date.now,
  online = () => typeof navigator === "undefined" || navigator.onLine,
}: StaleChunkReloadOptions = {}): boolean {
  if (!storage || !online()) return false;
  try {
    const last = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY));
    const at = now();
    if (
      Number.isFinite(last) &&
      last > 0 &&
      at - last >= 0 &&
      at - last < STALE_CHUNK_RELOAD_WINDOW_MS
    ) {
      return false;
    }
    storage.setItem(STALE_CHUNK_RELOAD_KEY, String(at));
  } catch {
    return false;
  }
  reload();
  return true;
}

function sessionStorageOrUndefined(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** How long a failed chunk waits before the recovery reload (see installStaleChunkRecovery). */
export const STALE_CHUNK_RELOAD_DELAY_MS = 2_500;

export interface StaleChunkRecoveryOptions extends StaleChunkReloadOptions {
  readonly delayMs?: number;
}

/**
 * Listens to Vite's `vite:preloadError` (a dynamic import or its CSS failed in the built app; Vite
 * wraps every dynamic import of the build, so this sees every failed chunk load) and reloads once.
 * The event is not prevented: the import still rejects with the chunk error, so the route error
 * component shows the new-version message (never a generic one) until the reload lands, and keeps
 * it with its Reload action when the guard refuses a second reload.
 *
 * The reload waits `delayMs` first. A chunk that fails because the page is being left is not a
 * stale build: once a navigation or a reload starts, Firefox and Safari cancel the page's pending
 * loads and reject its dynamic imports while the page still runs, and reloading at once would
 * replace the navigation the user asked for with a reload of the page they are leaving. When the
 * page really goes away its timers never run, and `pagehide` cancels the pending reload; when the
 * page comes back from the back/forward cache instead (`pageshow` with `persisted`), the cancelled
 * reload is armed again, as the rejected import does not repeat. A navigation that never unloads
 * the page (a download, Stop, a 204) leaves it alive, so a real stale build still recovers. Returns
 * the function that removes the listeners and cancels a pending reload.
 */
export function installStaleChunkRecovery(
  target: Pick<Window, "addEventListener" | "removeEventListener"> = window,
  { delayMs = STALE_CHUNK_RELOAD_DELAY_MS, ...options }: StaleChunkRecoveryOptions = {},
): () => void {
  let pending: ReturnType<typeof setTimeout> | undefined;
  // a recovery cancelled by pagehide: the page may come back from the back/forward cache, where the
  // rejected import does not repeat, so pageshow re-arms it
  let interrupted = false;
  const arm = () => {
    if (pending !== undefined) return;
    pending = setTimeout(() => {
      pending = undefined;
      reloadOnceForStaleChunk(options);
    }, delayMs);
  };
  const cancel = () => {
    if (pending !== undefined) clearTimeout(pending);
    pending = undefined;
  };
  const onPageHide = () => {
    interrupted = pending !== undefined;
    cancel();
  };
  const onPageShow = (event: Event) => {
    const restored = "persisted" in event && event.persisted === true; // PageTransitionEvent
    if (restored && interrupted) arm();
    interrupted = false;
  };
  target.addEventListener("vite:preloadError", arm);
  target.addEventListener("pagehide", onPageHide);
  target.addEventListener("pageshow", onPageShow);
  return () => {
    cancel();
    target.removeEventListener("vite:preloadError", arm);
    target.removeEventListener("pagehide", onPageHide);
    target.removeEventListener("pageshow", onPageShow);
  };
}
